import { Check, type LucideIcon } from "@/components/icons"
import { cn } from "@/lib/utils"

type WizardStepIndicatorProps = {
  steps: { key: string; label: string; icon: LucideIcon }[]
  currentStep: number
  onStepClick?: (step: number) => void
}

export function WizardStepIndicator({ steps, currentStep, onStepClick }: WizardStepIndicatorProps) {
  return (
    <div className="flex w-full items-center justify-center">
      {steps.map((step, index) => {
        const isCompleted = index < currentStep
        const isActive = index === currentStep

        let leftLineColor = "bg-border"
        if (index === 0) leftLineColor = "bg-transparent"
        else if (index <= currentStep) leftLineColor = "bg-primary/80"

        let rightLineColor = "bg-border"
        if (index === steps.length - 1) rightLineColor = "bg-transparent"
        else if (isCompleted) rightLineColor = "bg-primary/80"

        return (
          <div key={step.key} className="flex flex-1 flex-col items-center">
            {/* Circle row with connecting lines */}
            <div className="flex w-full items-center">
              <div
                className={cn(
                  "hidden h-[2px] flex-1 rounded-full transition-colors duration-500 sm:block",
                  leftLineColor
                )}
              />
              <button
                className="focus-visible:ring-ring shrink-0 rounded-full focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                disabled={!isCompleted && !isActive}
                type="button"
                onClick={() => isCompleted && onStepClick?.(index)}
              >
                <div
                  className={cn(
                    "flex h-11 w-11 items-center justify-center rounded-full text-sm font-medium shadow-sm transition-[color,background-color,box-shadow] duration-300",
                    isCompleted &&
                      "border-foreground bg-primary text-primary-foreground cursor-pointer border shadow-sm",
                    isActive &&
                      !isCompleted &&
                      "bg-primary/20 text-primary ring-primary ring-offset-background shadow-primary/25 shadow-md ring-2 ring-offset-2",
                    !isActive && !isCompleted && "bg-muted text-muted-foreground"
                  )}
                >
                  {isCompleted ? <Check className="h-5 w-5" /> : <step.icon className="h-5 w-5" />}
                </div>
              </button>
              <div
                className={cn(
                  "hidden h-[2px] flex-1 rounded-full transition-colors duration-500 sm:block",
                  rightLineColor
                )}
              />
            </div>
            {/* Label below */}
            <span
              className={cn(
                "mt-2 text-[13px] font-semibold whitespace-nowrap transition-colors duration-200",
                isActive || isCompleted ? "text-foreground" : "text-muted-foreground"
              )}
            >
              {step.label}
            </span>
          </div>
        )
      })}
    </div>
  )
}
